import React, { useEffect, useRef, useState } from "react";
import { CloseButton } from "react-bootstrap";
import { ModalType, useModal } from "../../Context/ModalContext";
import CanvasSignature from "../../components/Canvas/CanvasSignature";
import { useAppDispatch, useAppSelector } from "../../hook/redux";
import { createCanvasSignature } from "../Canvas/createCanvasSignature";
import deleteIcon from "../../assets/images/trash.png";

interface SignatureCanvasProps {
  base64: string | null;
  onClick: () => void;
  onDelete: () => void;
}

const SignatureCanvas: React.FC<SignatureCanvasProps> = ({ base64, onClick, onDelete }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && base64) {
      const ctx = canvasRef.current.getContext("2d");
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
          ctx.drawImage(img, 0, 0, canvasRef.current!.width, canvasRef.current!.height);
        }
      };
    }
  }, [base64]);

  return (
    <div style={ { position: "relative", width: 150, height: 75 } }>
      <canvas
        ref={ canvasRef }
        onClick={ onClick }
        width={ 150 }
        height={ 75 }
        style={ { cursor: "pointer", border: "1px solid #ccc" } }
      ></canvas>
      <button
        style={ {
          position: "absolute",
          top: -20,
          right: -15,
          padding: "0.4rem",
          background: "none",
          border: "#E8E3F9 solid 1px",
          borderRadius: "5px",
          zIndex: 2,
          cursor: "pointer"
        } }
        onClick={ onDelete }
      >
        <img
          src={ deleteIcon }
          alt="Supprimer"
          style={ {
            width: "20px",
            height: "20px"
          } }
        />
      </button>
    </div>
  );
};

function SignatureModal( { canvas } ) {
  const { selectedLayout, signatures } = useAppSelector( state => state.editor );

  const [ color, setColor ] = useState( "#000000" );

  const dispatch = useAppDispatch();

  const { show, toggleShow, type } = useModal();

  /**
   * Handles the color change event and updates the state with the selected color.
   *
   * @param {string} selectedColor - The new color selected by the user.
   * @returns {void}
   */
  const handleColorChange = (selectedColor: string): void => {
    setColor(selectedColor);
  };

  /**
   * Handles the signature selection in the SignatureModal component.
   * When a signature is clicked, it creates a canvas signature and then hides the modal.
   *
   * @param sign - The URL of the selected signature image.
   */
  const handleSignature = (sign: Partial<Signature>) => {
    const src = sign.base64;
    if (!src) return;
    createCanvasSignature(src, color, canvas, dispatch, selectedLayout)
    .then(() => {
      toggleShow(false, null, type);
    });
  };

  /**
   * Handles the background click event.
   *
   * This function is triggered when a user clicks on the background of a component. It ensures that the click
   * event is only processed if the click originates from the background itself and not from a child element.
   *
   * @param {React.MouseEvent} e - The mouse event triggered by the user's click.
   *     - `e.target` refers to the element that was clicked.
   *     - `e.currentTarget` refers to the element to which the event handler is attached.
   */
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      toggleShow(false, null, type);
    }
  };

  /**
   * Handles the deletion of a signature by dispatching an action
   * to remove a base64-encoded signature from the editor state.
   *
   * @param {Partial<Signature>} sign - An object containing the details of the signature to be deleted.
   * It is expected to contain a `base64` property representing the base64-encoded signature.
   */
  const handleDeleteSignature = (sign: Partial<Signature>) => {
    dispatch({
      type: "editor/removeBase64Signature",
      payload: {
        base64: sign.base64,
        color: "#000",
      },
    });
  };

  useEffect( () => {
    if ( signatures ) {
      signatures.forEach( ( sign, index ) => {
        const canvas = document.getElementById( `canvas-signature-${ index }` ) as HTMLCanvasElement;
      const context = canvas?.getContext("2d");

      if (canvas && context) {
        const image = new Image();
        image.src = String(sign);
        image.onload = () => {

          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);

          context.globalCompositeOperation = "source-in";
          context.fillStyle = color; // Appliquer la couleur choisie
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.globalCompositeOperation = "source-over"; // Remettre le mode par défaut
        };
      }
    });
    }
  }, [color, signatures, show]);

  return (
    <div
    className={`custom-modal-background ${show && type === ModalType.signature ? "" : "hidden"}`}
    onClick={handleBackgroundClick}
    >
      <div
        className={`custom-modal ${show && type === ModalType.signature ? "" : "hidden"}`}
        aria-labelledby="contained-modal-title-vcenter"
      >
      <div className="custom-modal-content">
        <div className="custom-modal-header">
          <h4 className="custom-modal-title">
            Insérer une signature
          </h4>
          <CloseButton
            onClick={ () => toggleShow( false, null, type ) }
            aria-label="Close"
          /> </div>
        <div className="custom-modal-body">
          <h4 className="custom-modal-title small">
            Vos signatures enregistrées :
          </h4>
          <div className="custom-modal-grid d-flex align-items-center px-3"
               style={{
                 overflowX: "auto",
                 overflowY: "hidden",
                 gap: "10px",
                 whiteSpace: "nowrap",
                 height: "110px"
               }}
          >
            {signatures?.map((sign, index) => (
              <SignatureCanvas
                key={index}
                base64={sign.base64}
                onClick={() => handleSignature(sign)}
                onDelete={() => handleDeleteSignature(sign)}
              />
            ))}
          </div>
          <div className="color-picker-container mt-3">
            <label htmlFor="color-picker" className="form-label">
              Sélectionnez une couleur :
            </label>
            <input
              type="color"
              value={ color }
              onChange={ ( e ) => handleColorChange( e.target.value ) }
              className="form-control"
              style={ { width: "50px", height: "50px", border: "none", cursor: "pointer" } }
            />
          </div>
          <div className="custom-modal-grid w-100">
            <CanvasSignature canvas={canvas} color={color} modalOpen={show && type === ModalType.signature} />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default SignatureModal;